// material ui
import {
  Button,
  Grid,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  withStyles,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled';
import PlayCircleOutlineIcon from '@material-ui/icons/PlayCircleOutline';
import SaveIcon from '@material-ui/icons/Save';
import copy from 'copy-to-clipboard';
import CSS from 'csstype';
import { cloneDeep, get } from 'lodash-es';
import MaterialTable, { MTableBody, MTableBodyRow, MTableEditRow } from 'material-table';
import moment from 'moment';
import React, { ReactElement } from 'react';
import { Link } from 'react-router-dom';

// api
import { IBidderGroupLabel } from '~/src/dash/api/bidder-group-label';
import { IAdserverCampaign } from '~/src/dash/api/campaign/campaign';
import { IChannelGroupLabel } from '~/src/dash/api/channel-group-label';
import { ISmallSite } from '~/src/dash/api/channels/channels_models';
import { IChannelGroup, IPublisher, ISite } from '~/src/dash/api/console-api';
import { IContextualSegmentLabel } from '~/src/dash/api/contextual-segments';
import { DealType } from '~/src/dash/api/deals';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
// utils
import { IHeaderBidderListInfo } from '~/src/dash/api/headerbidders/headerbidders_models';
import { EMonitorTriggerType } from '~/src/dash/api/monitors/monitors_models';
import { INetwork } from '~/src/dash/api/network';
import { ChipInventory } from '~/src/dash/components/channel-list';
// components
import { ModelDialogActiveView } from '~/src/dash/components/model-dialog-active-view';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { ModelMediumButtonStyle } from '~/src/dash/components/model-medium-button';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
// responsiveness
import {
  getListTableMediumResponsiveColumnWidth,
  RENDERTYPE_MEDIUM_WIDTH,
} from '~/src/dash/components/tables/ListTable/common';
import { RenderTypeTruncate } from '~/src/dash/components/tables/ListTable/common';
import { ct, isWithinMediaSizeOrBelow, TooltipWithTranslation } from '~/src/dash/publicaUI';
// other
import { IStore } from '~/src/dash/reducers/store';
import { getColorTheme } from '~/src/dash/resellers/utils/importResellerConfig';
import { IMenuList } from '~/src/dash/types/menuList';
import {
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { ECampaignStatus } from '~/src/dash/utils/campaign-status';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatYearMonthNameDate } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { formatForClassname, removeIDForClassname } from '~/src/dash/utils/format';
import { allowedEmptyIPermissionCheck, IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { getMediafileFromVastURL } from '~/src/dash/utils/vast';
import { BaseContextState, IBaseState } from '~/src/dash/views/base/contexts';
import { IIASFirstAndThirdPartyData } from '~/src/dash/views/common/ias-data-types';
import { IconV2 } from '~/src/dash/components/icons/IconV2';

// COMPONENT DESCRIPTION: ListTable to be used to display a list of resources like networks, bidders, campaigns, etc.
// For information regarding props available for 'columns' refer to https://material-table.com/#/docs/get-started
// Add new 'RenderTypes' to generateRender if a there is a new type of cell
// Add new FieldNames for additional field values to be shown in columns

// use bignumber or percent if the item shown is a number that needs to be converted to string
// which is important for sorting
export type RenderType =
  | ''
  | 'normal'
  | 'bold'
  | 'namelink'
  | 'namelink_returnsnameid'
  | 'overunderlink'
  | 'activeswitch'
  | 'deleteclone'
  | 'copy'
  | 'delete'
  | 'bignumber'
  | 'percent'
  | 'date'
  | 'activeswitchmodal'
  | 'clone'
  | 'modal'
  | 'editable'
  | 'editable1'
  | 'editable2'
  | 'play'
  | 'playCreativeURL'
  | 'creativeURL'
  | 'vastPlay'
  | 'namelink_withtooltip'
  | 'modal2'
  | 'truncate'
  | 'truncate2'
  | 'modal3'
  | 'superuserswitch'
  | 'currency'
  | 'markedflag'
  | 'currencyOrUndefined'
  | 'textchips'
  | 'icons'
  | 'actions';

interface IProps extends IReduxActionProps, ISessionStateProps, IUserStateProps, IWithStylesProps {
  // title of the table
  title: string;
  // resource of the table, used to interpolate class names for testing
  resource?: string;
  // type for items displayed
  items: INetwork[];
  // head cells field, title and type
  columns: IListTableColumn[];
  // data to render
  data: ListTableRowData[];
  // determines whether to show empty rows in the table or just truncate the table height
  // to the last visible row
  emptyRowsWhenPaging?: boolean;
  // determines if table will try to render
  isLoading: boolean;
  // determines if table should not allow api calls during this time
  isUpdating: boolean;
  // permissions
  isSuperUser: boolean;
  readRoleName?: string; // Role name to check if the user has the right to read the object (ex: 'read_bidder').
  writeRoleName?: string; // Role name to check if the user has the right to write the object (ex: 'write_bidder').
  // optional msg if delete button disable is active
  disableDeleteMsg?: string;
  // optional msg if edit button disable for the modal in RenderType 'actions' is active
  disableEditModalMsg?: string;
  // determines if selection checkboxes are enabled
  selectionEnabled?: boolean;
  // enables fixed table layout where width must be provided
  // YOU CAN SET A FIXED WIDTH WITH PERCENTAGES WHICH IS REALLY NICE!
  fixedEnabled?: boolean;
  // gets the item's setting route
  getItemSettingRoute: (id: string | number) => string;
  // handlers
  handleChangeItem?: (field: string, itemID: string, value: any) => void;
  handleChangeActive?: (itemID: string | number, checked: boolean) => void;
  handleSuperOnlyChange?: (itemID: string | number, checked: boolean) => void;
  handleDeleteItem?: (itemID: string, idx?: number) => Promise<any>;
  // used in renderType 'modal' to open the modal
  handleOpenModal?: (itemID: string, idx?: number) => void;
  // used in renderType 'modal2' to open the modal
  handleOpenModal2?: (itemID: string, idx?: number) => void;
  // used in renderType 'modal3' to open the name link modal
  handleOpenModal3?: (itemID: string | number, idx?: number) => void;
  // used in renderType 'modal3' to custom style the Grid contents
  modal3GridStyles?: React.CSSProperties;
  // used in the change active modal (needs to return promise)
  handleActiveItem?: (itemID: string) => Promise<any>;
  // used in renderTypes with 'clone'
  handleCloneItem?: (itemID: string | number) => void;
  // used when material table selection is enabled to pass which rows are selected back to the parent
  handleSelectionChange?: (rows: ListTableRowData[], selectedRow: ListTableRowData) => void;
  // used to change a row to being "editable" in 'editable' render type
  handleChangeEditable?: (field: string, id: string, value: any) => void;
  // sets parent component to updating
  handleSetUpdating: (updating: boolean) => void;
  // used to track events on name link click
  handleNameLinkClick?: (name: string) => void;
  // used to track events on name link click
  handleNameLinkClickID?: (id: string) => void;
  // used to track events on name link click
  handleNameLinkClickInfo?: (name: string, id: string) => void;
  // pass in custom toolbar to show at the top of the modal
  renderCustomToolbar?: (props: any) => ReactElement;
  // pass in custom header to show at the top of the modal
  renderCustomHeader?: (props: any) => ReactElement;
  // moves search bar to left side
  enableSearchBarLeft?: boolean;
  // default rows per page
  pageSize?: number;
  // page size options at the bottom of the table
  pageSizeOptions?: number[];
  // set a truncate length
  truncateLength?: number;
  // editable onRowAdd, onRowUpdate and onRowDelete functions
  editable?: any;
  // which columns should the actions appear in
  actionsColumnIndex?: number;
  // shows "+ ADD ROW" instead of a plus sign
  useCustomAddButton?: boolean;
  // centers the action row items
  enableCenterActionCells?: boolean;
  // Handle error in parent. For example display a snackbar error.
  handleError?: (errorMsg: string) => void;
  publisher?: IPublisher;
  disableSnackbar?: boolean;
  secondTabSelection?: boolean;
  // disables focus for ComponentDidUpdate lifecycle
  disableUpdateFocus?: boolean;
  // pagination
  enablePagination?: boolean;
  queryData?: () => Promise<any>;
  // ref to the underlying MaterialTable component
  // this is used to force-refresh it when we want it to display new data
  tableRef?: React.RefObject<HTMLDivElement>;
  // add functionality to the on click header sort
  onOrderChange?: (id: number) => void;
}

interface IState {
  // pagination, current page and rows per page shown
  page: number;
  rowsPerPage: number;
  // sorting
  orderBy: string;
  orderDirection: 'asc' | 'desc';
  // delete modal params
  deleteModalActive: boolean;
  deleteModalID: string;
  deleteModalIdx?: string | number;
  deleteModalName: string;
  // active modal params
  activeModalActive: boolean;
  activeModalID: string | number;
  activeModalName: string;
  itemCurrentlyActive: boolean;
  // shows which rowdata id is hovered
  hoverID: string;
  editableVal: string;
  editableRowID: string;
  editableColField: string;
}

// Used to override the color when disabled to look less jarring
// when quickly changing the active state of the switch
// Used For: Active Switch, Super User Switch
const CustomSwitch = withStyles({
  switchBase: {
    '&$disabled': {
      color: '#fafafa',
    },
  },
  checked: {},
  track: {},
  disabled: {},
})(Switch);

// Add additional row data types here when creating additional lists as well as to type FieldNames
export type FieldNames =
  | 'Active'
  | 'Name'
  | 'ID'
  | 'UUID'
  | 'Network'
  | 'InventorySplit'
  | 'Actions'
  | 'IDName'
  | 'FreewheelPublisherID'
  | 'IDString'
  | 'Advertiser'
  | 'StartDate'
  | 'EndDate'
  | 'Impressions'
  | 'BidErrors'
  | 'BidErrorBlockedResponses'
  | 'BidErrorsBrandSafety'
  | 'BidderID'
  | 'DealKey'
  | 'BidderType'
  | 'CustomRender'
  | 'TimeFrame'
  | 'IP'
  | 'Email'
  | 'Status'
  | 'Progress'
  | 'CPM'
  | 'DeliveredImps'
  | 'Goal'
  | 'Pacing'
  | 'Duration'
  | 'AspectRatio'
  | 'Dimensions'
  | 'URL'
  | 'Bitrate'
  | 'SiteID'
  | 'SiteName'
  | 'SiteWeight'
  | 'Percentage'
  | 'Variants'
  | 'GroupedChannels'
  | 'ReportType'
  | 'LastUpdated'
  | 'Recipients'
  | 'Frequency'
  | 'Period'
  | 'AdserverCreatives'
  | 'UpdatedAt'
  | 'ChannelGroupLabels'
  | 'SearchChannelGroupLabels'
  | 'BidderIDs'
  | 'AllowedAdomains'
  | 'AllowedIABCategories'
  | 'GroupedBidders'
  | 'BidderGroupLabels'
  | 'SearchBidderGroupLabels'
  | 'Partner'
  | 'PublishersOptedIn'
  | 'SubSites'
  | 'SuperSiteTargeting'
  | 'SuperSiteTargetingType'
  | 'PriceKey'
  | 'PriceValue'
  | 'TargetedChannels'
  | 'TargetedChannelGroups'
  | 'Priority'
  | 'Bidder'
  | 'SuperUserOnly'
  | 'SubCategory'
  | 'eCPM'
  | 'Revenue'
  | 'SiteShareOfImpressions'
  | 'CPMFactor'
  | 'ImpressionFactor'
  | 'MarkedAsBooked'
  | 'CountriesTargeted'
  | 'PublisherID'
  | 'LiverampCustomID'
  | 'FlexibleBiddingEnabled'
  | 'DealType'
  | 'AuctionType'
  | 'SpotxTierEnabled'
  | 'MagniteCTVTierEnabled'
  | 'Destinations'
  | 'VenueID'
  | 'MultiplierAmount'
  | 'VenueLatitude'
  | 'VenueLongitude'
  | 'EPGChannelID'
  | 'PublicaChannelID'
  | 'PublicaChannelName'
  | 'ContentChannel'
  | 'FloorPrice'
  | 'DeduplicationOptions'
  | 'Labels'
  | 'Deals'
  | 'Headerbidders'
  | 'LabelSearch'
  | 'Icons'
  | 'CreativeURL'
  | 'OriginalCreativeURL'
  | 'Adomain'
  | 'IABCategory'
  | 'MonitorCustomMetricA'
  | 'MonitorCustomMetricB'
  | 'MonitorCustomMetricFormula'
  | 'Product'
  | 'IsTiered'
  | 'MinPrice'
  | 'CustomCampaignPrice'
  | 'CustomVastPrice'
  | 'PricingTier'
  | 'TiersImpressionMax'
  | 'TierPrice'
  | 'Notes'
  | 'PastWeekRevenue'
  | 'AppBundleID'
  | 'ExemptPublisherIDs'
  | 'ExemptResellerIDs'
  | 'PastWeekImpressions'
  | 'PastWeekAverageCPM'
  | 'PastWeekRevenueSites'
  | 'PastWeekEndpointRequests'
  | 'Last24HrImpressions'
  | 'Last24HrAverageCPM'
  | 'Last24HrRevenue'
  | 'Last24HrEndpointRequests'
  | 'DemandSource'
  | 'RateLimit'
  | 'CollectionTitles'
  | 'Custom10Targeting'
  | 'Custom10TargetingV2'
  | 'Custom10TargetingAlias'
  | 'Edit'
  // the following names are used in the IAS Whitelabel
  | 'Advertisers'
  | 'Creatives'
  | 'LineItems'
  | 'OrderName'
  | 'TotalImps'
  | 'RenderedImps'
  | 'RefreshImps'
  | 'MeasuredRate'
  | 'ViewabilityRate'
  | 'IVTRate'
  | 'DataSize'
  | 'CurrentValue'
  | 'PreviousValue'
  | 'ResultValue'
  | 'UniqueUserCount';

export type ListTableRowData =
  | INetworkRowData
  | IDealRowData
  | ICampaignRowData
  | IIPTraceRowData
  | IAdserverOrderRowData
  | IMediafileRowData
  | IChannelGroupRowData
  | IBrandSafetyRuleExceptionRowData
  | IBidderGroupRowData
  | IHeaderBidderRowData
  | IForecastRowData
  | IMonitorRowData
  | IInventoryRouteRowData
  | IContextualSegmentRowData
  | IMonitorCustomMetricRowData
  | IGlobalAppBundleBlockListRowData;

// Each kind of table can have a custom interface for the data passed into to render
// put any field that needs to be in every row data in IGeneralRowData
export interface IGeneralRowData {
  // enable processing data by idx
  idx?: number;
  EnableEditable?: boolean;
  DisableDelete?: boolean;
  EnableLink?: boolean;
  BoldRow?: boolean;
  SubCategory?: string;
  ID: string | number;
  Name: string;
  IsActiveSwitchDisabled?: boolean; // do we want to disable the "Active" switch
  DisableEditModal?: boolean; // disable popup modal button for editing in RenderType 'actions'
}

export interface IDealDemandPartnerRowData extends IGeneralRowData {
  APIToken: string;
}

export interface IBrandSafetyRuleExceptionRowData extends IGeneralRowData {
  AllowedAdomains: string;
  AllowedIABCategories: string;
  BidderIDs: number;
  ID: string | number;
  Name: string;
  Active?: boolean;
}

export interface IBrandSafetyRuleAdomainExceptionRowData extends IGeneralRowData {
  Adomain: string;
  ID: string | number;
  Name: string;
}

export interface INetworkRowData extends IGeneralRowData {
  Active: boolean;
  UUID?: string;
  Network?: INetwork;
  InventorySplit?: number;
  IDName?: string;
  FreewheelPublisherID?: string;
}

export interface IAudienceSegmentRowData extends IGeneralRowData {
  Active: boolean;
}

export interface IAPSPriceMapPairRow extends IGeneralRowData {
  PriceKey: string;
  PriceValue: number;
}

export interface IDemandSourceRateLimitMapPairRow extends IGeneralRowData {
  DemandSource: string;
  RateLimit: number;
}

export interface IDOOHVenueMultiplierMapPairRow extends IGeneralRowData {
  Name: string;
  ID: string;
  MultiplierAmount: number;
  VenueID: string;
  VenueLatitude: number;
  VenueLongitude: number;
}

export interface IContentCollectionMapPairRow extends IGeneralRowData {
  Name: string;
  CollectionTitles: number;
}

export interface IContentCollectionDataMapPairRow extends IGeneralRowData {
  Custom10TargetingAlias: string;
  Custom10TargetingV2: string;
  Custom10Targeting: number;
}

export interface IGlobalAppBundleBlockListRowData extends IGeneralRowData {
  Active: boolean;
  ID: string;
  AppBundleID: string;
  ExemptPublisherIDs: string;
  ExemptResellerIDs: string;
  ExemptPublishersMenuList: IMenuList;
  ExemptResellersMenuList: IMenuList;
  shouldUpdate?: boolean;
}

export interface IEPGChannelMapPairRow extends IGeneralRowData {
  Name: string;
  ID: string;
  EPGChannelID: string;
  PublicaChannelID: number;
  PublicaChannelName: string;
  ContentChannel: string;
}

export interface ILiverampCustomIDMapPairRow extends IGeneralRowData {
  PublisherID: number;
  LiverampCustomID: number;
}

export interface IForecastRowData extends IGeneralRowData {
  SiteName: string;
  Active: boolean;
  SubCategory: string;
  Impressions: number;
  SiteShareOfImpressions: number;
  eCPM: number | undefined;
  Revenue: number | undefined;
  CPMFactor: number | undefined;
  ImpressionFactor: number;
  EnableEditable: boolean;
  MarkedAsBooked: boolean | undefined;
}

export interface ICampaignRowData extends IGeneralRowData {
  Active: boolean;
  Status: ECampaignStatus;
  Progress: string;
  StartDate: moment.Moment;
  EndDate: moment.Moment;
  CPM: number | string;
  DeliveredImps: number;
  Goal: number;
  Campaign: IAdserverCampaign;
  Pacing: number;

  // Tooltip stuff
  TooltipEnabled?: boolean;
  ToolTipText?: string;
}

export interface IDealRowData extends IGeneralRowData {
  Active: boolean;
  DealKey: string;
  DealType: DealType;
  BidderType: string;
  Channels: ISite[] | ISmallSite[];
  ChannelGroups: IChannelGroup[];
}

export interface IPublishersByPartnerRowData extends IGeneralRowData {
  Partner: string;
  PublishersOptedIn: number;
}

export interface IIPTraceRowData extends IGeneralRowData {
  Active?: boolean;
  IP: string;
  Email: string;
  UUID: string;
  UserID: string;
  StartDate: string;
}

export interface IAdserverOrderRowData extends IGeneralRowData {
  Active: boolean;
  LineItems: IAdserverCampaign[];
  Advertiser: string;
  StartDate: moment.Moment;
  Impressions: number;
}

export interface ISiteRowData extends IGeneralRowData {
  SubSites: number;
  Site: ISite;
}

export interface ISiteAdserverCampaignAssociationRowData extends IGeneralRowData {
  SiteID: number;
  SiteName: string;
  SiteWeight: number;
  Percentage: number;
}

export interface IMediafileRowData extends IGeneralRowData {
  Active: boolean;
  Advertiser: string;
  AdvertiserID: number;
  Duration: string;
  AspectRatio: string;
  Dimensions: string;
  Bitrate: string;
  // this is specially defined from material-table, how they pass checked for selection
  tableData: { [checked: string]: boolean };
  URL: string;
  MimeType: string;
}

export interface IVastRowData extends IGeneralRowData {
  URL: string;
  AdserverCreatives: number;
  Advertiser: string;
  AdvertiserID: number;
  UpdatedAt: moment.Moment;
  // this is specially defined from material-table, how they pass checked for selection
  tableData: { [checked: string]: boolean };
}

export interface IReportRowData extends IGeneralRowData {
  Active: boolean;
  ReportType: string;
  LastUpdated: moment.Moment;
  Recipients: number;
  Frequency: string;
  Period: string;
}

export interface IChannelGroupRowData extends IGeneralRowData {
  Active?: boolean;
  GroupedChannels: number;
  ChannelGroupLabels: IChannelGroupLabel[];
  SearchChannelGroupLabels: string;
  tableData: { checked: boolean };
}

export interface IIASDashboardTableRowData extends IGeneralRowData {
  [key: string]: any;
}

export interface IMeasurementTableRowData extends IGeneralRowData, IIASFirstAndThirdPartyData {}

export interface ICountryGroupRowData extends IGeneralRowData {
  Active?: boolean;
  ID: string;
  Name: string;
  CountriesTargeted: number;
}

export interface IMonitorCustomMetricRowData extends IGeneralRowData {
  Active?: boolean;
  Name: string;
  ID: string;
  MonitorCustomMetricA: string;
  MonitorCustomMetricB: string;
  MonitorCustomMetricFormula: string;
}

export interface IBidderGroupRowData extends IGeneralRowData {
  Active?: boolean;
  GroupedBidders: number;
  BidderGroupLabels: IBidderGroupLabel[];
  SearchBidderGroupLabels: string;
}

export interface IHeaderBidderRowData extends IGeneralRowData {
  Active: boolean;
  Bidder: string;
  Priority: number;
  TargetedChannels: number;
  TargetedChannelGroups: number;
  SuperUserOnly: boolean;
  HeaderBidder: IHeaderBidderListInfo;
  PastWeekRevenue: string;
  // this is specially defined from material-table, how they pass checked for selection
  tableData: { [checked: string]: boolean };
}

export interface IMonitorRowData extends IGeneralRowData {
  Active: boolean;
  Status: EMonitorTriggerType;
  SuperUserOnly: boolean;
  // this is specially defined from material-table, how they pass checked for selection
  tableData: { [checked: string]: boolean };
}

export interface IInventoryRouteRowData extends IGeneralRowData {
  Active: boolean;
  Destinations: number;
}

export interface ICreativeValidationRowData extends IGeneralRowData {
  PublisherID: number;
  Adomain: string;
  AdomainEnrichedBy: string;
  AdomainOverride: string;
  IABCategory: string;
  IABCategoryEnrichedBy: string;
  IABCategoryCodes: string;
  IABCategoryOverride: string;
  OriginalCreativeURL: string;
  CreativeURL: string;
  Impressions: number;
  BidErrors: number;
  BidErrorBlockedResponses: number;
  BidErrorsBrandSafety: number;
  BidderID: number;
  BidderName: string;
  TotalCount: number;
  ReviewStatus: number;
  Notes: string;
  BrandSafetyRuleIDs: number[];
  UpdatedAt: string;
  User: string;
}

export interface IContextualSegmentRowData extends IGeneralRowData {
  Active: boolean;
  Deals: number;
  Headerbidders: number;
  LineItems: number;
  Labels: IContextualSegmentLabel[];
  LabelSearch: string;
  Icons: string[];
}

// IChannelRowData defines the row data when the component is used with a channel list.
export interface IChannelRowData extends IGeneralRowData {
  Active: boolean;
  FloorPrice: number;
  DeduplicationOptions: string[];
  Labels: string[];
  UUID: string;
  // defined for the material-table
  tableData: { [checked: string]: boolean };
  TotalCount: number;
}

export interface IBillingDetailConfigurationRowData extends IGeneralRowData {
  Product: string;
  IsTiered: string;
  MinPrice: string;
  CustomCampaignPrice: string;
  CustomVastPrice: string;
}

export interface IBillingDetailTiersRowData extends IGeneralRowData {
  Product: string;
  PricingTier: string;
  TiersImpressionMax: string;
  TierPrice: string;
}

const iconMap: IIconMap = {
  IAS: <img src='/assets/shared/png/ias.png' alt='IASImage' style={{ height: 50 }} />,
  Iris: <img src='/assets/shared/png/iris.png' alt='Iris Image' style={{ height: 50 }} />,
  Comscore: (
    <img src='/assets/shared/png/comscore.png' alt='Comscore Image' style={{ height: 20 }} />
  ),
};

interface IIconMap {
  IAS: ReactElement;
  Iris: ReactElement;
  Comscore: ReactElement;
}

export type IconMapKey = 'IAS' | 'Iris' | 'Comscore';
export const IconMapKeyIAS = 'IAS';
export const IconMapKeyIris = 'Iris';
export const IconMapKeyComscore = 'Comscore';

const selectHRef = (url: string, originalURL: string): string => {
  if (url.startsWith('https://') || url.startsWith('http://')) {
    return url;
  }
  return originalURL;
};

// DO NOT MAKE ADDITIONAL INTERFACES, add to FieldNames for additional fields
// define all fields for columns here
export interface IListTableColumn {
  field: FieldNames;
  title: string;
  tooltip?: string;
  EnableEditable?: boolean;
  field2?: FieldNames;
  renderType?: RenderType;
  render?: (
    rowData:
      | IContentCollectionDataMapPairRow
      | INetworkRowData
      | IAdserverOrderRowData
      | IChannelGroupRowData
      | IBidderGroupRowData
      | IHeaderBidderRowData
      | IMonitorRowData
      | ISiteRowData
      | ICreativeValidationRowData
      | IGlobalAppBundleBlockListRowData
      | IDemandSourceRateLimitMapPairRow,
  ) => any;
  searchable?: boolean;
  // customSort: () => 0 use this when you want to only use backend sorting
  customSort?: (v1: any, v2: any) => number;
  cellStyle?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  paddingLeft?: string;
  maxWidth?: string;
  minWidth?: string;
  width?: string;
  hidden?: boolean;
  sorting?: boolean;
  initialEditValue?: any;
  defaultSort?: 'asc' | 'desc';
  textAlignHeader?: 'left' | 'center' | 'right';
  textAlign?: 'left' | 'center' | 'right';
  editComponent?: any;
}

export const ListTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      button: ModelMediumButtonStyle,
      menuButtonText: {
        marginBottom: 2,
        paddingRight: 8,
        fontSize: '0.875rem',
        fontWeight: 500,
        color: '$434343',
      },
      tableRow: {
        '& td div': {
          justifyContent: 'center',
        },
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    static contextType = BaseContextState;

    constructor(props: IProps) {
      super(props);
      this.state = {
        page: 0,
        rowsPerPage: 25,
        orderBy: 'Priority',
        orderDirection: 'asc' as 'asc' | 'desc',
        deleteModalActive: false,
        deleteModalID: '',
        deleteModalIdx: '',
        deleteModalName: '',
        activeModalActive: false,
        activeModalID: '',
        activeModalName: '',
        itemCurrentlyActive: false,
        hoverID: '',
        editableVal: '',
        editableRowID: '',
        editableColField: '',
      };
    }

    componentDidMount() {
      const searchBar = document.querySelector('.MTableToolbarWrapper input') as HTMLElement;
      if (searchBar) {
        searchBar.focus();
      }
    }

    componentDidUpdate() {
      if (!this.props.disableUpdateFocus) {
        let searchBar;
        // toggle which search bar to focus on pages with multiple tabs of list tables
        if (this.props.secondTabSelection) {
          const searchBarList = document.querySelectorAll('.MTableToolbarWrapper input');
          if (searchBarList.length >= 1) {
            searchBar = searchBarList[1] as HTMLElement;
          } else {
            searchBar = null;
          }
        } else {
          searchBar = document.querySelector('.MTableToolbarWrapper input') as HTMLElement;
        }
        if (searchBar) {
          searchBar.focus();
        }
      }
    }

    // -------- COMPONENT HELPER FUNCTIONS -------- //

    setCellEditable = (editableRowID: string, editableColField: string) => {
      this.setState({
        editableRowID,
        editableColField,
      });
    };

    isCellEditable = (editableID: string, field: string) => {
      return this.state.editableRowID === editableID && this.state.editableColField === field;
    };

    // convertStringOrNumberToString convert a string | number type to a string type.
    convertStringOrNumberToString = (inputValue: string | number) => {
      // if inputValue is a number, then convert it to string
      if (typeof inputValue === 'number') {
        return inputValue.toString();
      }
      // else, keep is as is
      return inputValue;
    };

    setHover = (hoverID: string, hover: boolean) => {
      if (hover) {
        this.setState({
          hoverID,
        });
      } else {
        this.setState({
          hoverID: '',
        });
      }
    };

    // generates custom renders for a table cell if a renderType is defined
    // all render functions are offset by 25 pixels due to "hidden" sorting arrow on the column header
    generateRender(
      column: IListTableColumn,
      writeRoleName: string | undefined,
      isMediumSizeOrBelow: boolean,
    ): ((rowData: ListTableRowData) => ReactElement) | undefined {
      // Check if the user has the write permission. If the name is 'undefined' we assume the user always has access.
      let writeAuthorization = allowedEmptyIPermissionCheck;
      if (writeRoleName !== undefined) {
        writeAuthorization = IS_AUTHORIZED([writeRoleName], this.props.user.user); // Role is defined, get 'real' array value.
      }

      // get publisher object
      const publisher = this.props.publisher;

      // get currentcy and exchange rate from publisher, or use default values
      let currencySymbol = '$';
      let exchangeRate = 1;
      if (publisher) {
        exchangeRate = publisher.ExchangeRate.ExchangeValueToUSD;
        currencySymbol = convertECurrencyTypeToSymbol(publisher.CurrencyDisplayed);
      }

      switch (column.renderType) {
        // normal size text
        case 'normal':
          return (rowData: ListTableRowData) => {
            return (
              <Typography
                style={{
                  fontSize: 14,
                  fontWeight: rowData.BoldRow ? 500 : 400,
                  marginRight: 25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {get(rowData, column.field)}
              </Typography>
            );
          };
        // truncate text and use tooltip for full name
        case 'truncate':
          return (rowData: ListTableRowData) => {
            const truncateLength = this.props.truncateLength || 30;
            const name = get(rowData, column.field);
            let truncatedName = name;
            if (truncatedName !== null && truncatedName.length > truncateLength) {
              truncatedName = truncatedName.slice(0, truncateLength) + '...';
            }
            if (rowData.EnableLink && 'URL' in rowData) {
              return (
                <Tooltip title={name}>
                  <a
                    href={rowData.URL}
                    style={{
                      color: 'black',
                    }}
                    target='_blank'
                  >
                    <Typography
                      style={{
                        fontSize: 14,
                        fontWeight: rowData.BoldRow ? 500 : 400,
                        marginRight: 25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {truncatedName}
                    </Typography>
                  </a>
                </Tooltip>
              );
            }
            return (
              <Tooltip title={name}>
                <Typography
                  style={{
                    fontSize: 14,
                    fontWeight: 400,
                    marginRight: 25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {truncatedName}
                </Typography>
              </Tooltip>
            );
          };
        // different version of truncate
        case 'truncate2':
          return (rowData: ListTableRowData) => {
            const name = get(rowData, column.field) || '';
            const stringSplit = String(name).split('),');
            const truncatedName = stringSplit[0] + ')';
            return <RenderTypeTruncate name={String(name)} truncatedName={truncatedName} />;
          };
        // normal size text, editable
        case 'editable':
          return (rowData: ListTableRowData) => {
            const isCellEditable = this.isCellEditable(rowData.ID.toString(), column.field);
            return (
              <Grid item style={{ width: '100%' }}>
                <Grid
                  container
                  direction='row'
                  alignItems='center'
                  onMouseEnter={() => this.setHover(rowData.ID.toString(), true)}
                  onMouseLeave={() => this.setHover(rowData.ID.toString(), false)}
                  onClick={() => {
                    this.setState({ editableVal: get(rowData, column.field) });
                    if (!isCellEditable) {
                      this.setCellEditable(rowData.ID.toString(), column.field);
                    }
                  }}
                >
                  <Grid
                    item
                    xs={11}
                    style={
                      this.state.hoverID === rowData.ID && !rowData.EnableEditable
                        ? {
                            backgroundColor: 'rgb(248, 248, 248)',
                          }
                        : {}
                    }
                  >
                    {isCellEditable ? (
                      <TextField
                        value={this.state.editableVal}
                        fullWidth
                        multiline
                        onChange={(event) => {
                          this.setState({ editableVal: event.target.value });
                          if (this.props.handleChangeItem) {
                            this.props.handleChangeItem(
                              rowData.ID.toString(),
                              column.field,
                              event.target.value,
                            );
                          }
                        }}
                        inputProps={{
                          style: {
                            textAlign: 'center',
                          },
                        }}
                        InputProps={{
                          disableUnderline: true,
                          style: {
                            fontSize: 14,
                            fontWeight: rowData.BoldRow ? 500 : 400,
                            marginRight: 25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            border: rowData.SubCategory !== undefined ? '1px solid lightgray' : '',
                          },
                        }}
                      />
                    ) : (
                      <Typography
                        style={{
                          fontSize: 14,
                          fontWeight: rowData.BoldRow ? 500 : 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          color: '#2B2B2B',
                          border: rowData.SubCategory !== undefined ? '1px solid lightgray' : '',
                        }}
                      >
                        {get(rowData, column.field)}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={1}>
                    {this.state.hoverID === rowData.ID &&
                      (isCellEditable ? (
                        <SaveIcon
                          style={{ color: '#707070' }}
                          onClick={() => {
                            // sets editableCell to no cell
                            this.setCellEditable('', '');
                            if (this.props.handleChangeEditable) {
                              this.props.handleChangeEditable(
                                rowData.ID.toString(),
                                column.field,
                                this.state.editableVal,
                              );
                            }
                          }}
                        />
                      ) : (
                        <EditIcon style={{ color: '#707070' }} />
                      ))}
                  </Grid>
                </Grid>
              </Grid>
            );
          };
        // normal size text, opens modal on click
        case 'modal':
        case 'modal2':
          const handleOpenModal =
            column.renderType === 'modal'
              ? this.props.handleOpenModal
              : this.props.handleOpenModal2;
          return (rowData: ListTableRowData) => {
            return (
              <Typography
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  marginRight: 25,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'pointer',
                }}
                onClick={() => {
                  if (handleOpenModal) {
                    handleOpenModal(rowData.ID.toString(), rowData.idx);
                  }
                }}
              >
                {get(rowData, column.field)}
              </Typography>
            );
          };
        // link to play video file
        case 'play':
          return (rowData: ListTableRowData) => {
            return (
              <a
                href={get(rowData, column.field)}
                style={{
                  color: 'black',
                  marginRight: 22,
                }}
                target='_blank'
              >
                <PlayCircleFilledIcon />
              </a>
            );
          };

        // show the URL
        case 'creativeURL':
          return (rowData: ListTableRowData) => {
            return (
              <TooltipWithTranslation tKey='Copy'>
                <Typography
                  onClick={() => copy((get(rowData, column.field) || '').toString())}
                  style={{
                    fontSize: 14,
                    fontWeight: rowData.BoldRow ? 500 : 400,
                    marginRight: 25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer',
                  }}
                >
                  {get(rowData, column.field)}
                </Typography>
              </TooltipWithTranslation>
            );
          };

        // link to play video file
        case 'playCreativeURL':
          return (rowData: ListTableRowData) => {
            return (
              <a
                href={selectHRef(get(rowData, 'CreativeURL'), get(rowData, 'OriginalCreativeURL'))}
                style={{ color: publicaColors.darkGray }}
                target='_blank'
              >
                <PlayCircleOutlineIcon />
              </a>
            );
          };

        // Display a button to call the VAST URL and extract the mediafile URL, and then open the content.
        case 'vastPlay':
          return (rowData: ListTableRowData) => {
            return (
              <IconButton
                onClick={() => this.handleVastPlayButtonClick(get(rowData, column.field))}
                style={{ color: 'black' }}
              >
                <PlayCircleFilledIcon />
              </IconButton>
            );
          };
        // normal size text but percent (USE NUMBER TYPE)
        // if a field2 is provided, a tooltip will appear
        case 'percent':
          return (rowData: ListTableRowData) => {
            if (column.field2) {
              return (
                <Tooltip title={get(rowData, column.field2)}>
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: rowData.BoldRow ? 500 : 400,
                      marginRight: 25,
                    }}
                  >
                    {get(rowData, column.field).toFixed(2) + '%'}
                  </Typography>
                </Tooltip>
              );
            } else {
              if (column.field === 'SiteShareOfImpressions' && get(rowData, column.field) === 0) {
                return (
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: rowData.BoldRow ? 500 : 400,
                      marginRight: 25,
                    }}
                  >
                    N/A
                  </Typography>
                );
              }
              return (
                <Typography
                  style={{ fontSize: 14, fontWeight: rowData.BoldRow ? 500 : 400, marginRight: 25 }}
                >
                  {get(rowData, column.field).toFixed(2) + '%'}
                </Typography>
              );
            }
          };
        // normal size text but number with commas (USE NUMBER TYPE)
        // if a field2 is provided, a tooltip will appear
        case 'bignumber':
          return (rowData: ListTableRowData) => {
            if (column.field2) {
              return (
                <Tooltip title={get(rowData, column.field2)}>
                  <Typography
                    style={{
                      fontSize: 14,
                      fontWeight: rowData.BoldRow ? 500 : 400,
                      marginRight: 25,
                    }}
                  >
                    {get(rowData, column.field).toLocaleString()}
                  </Typography>
                </Tooltip>
              );
            } else {
              return (
                <Typography
                  style={{ fontSize: 14, fontWeight: rowData.BoldRow ? 500 : 400, marginRight: 25 }}
                >
                  {get(rowData, column.field).toLocaleString()}
                </Typography>
              );
            }
          };
        // normal size text but number with commas (USE NUMBER TYPE)
        // if a field2 is provided, a tooltip will appear
        case 'currency':
          return (rowData: ListTableRowData) => {
            let value = get(rowData, column.field);
            if (Number.isNaN(value)) {
              value = 0;
            }
            return (
              <Typography
                style={{ fontSize: 14, fontWeight: rowData.BoldRow ? 500 : 400, marginRight: 25 }}
              >
                {currencySymbol + (Math.round(value * exchangeRate * 100) / 100).toLocaleString()}
              </Typography>
            );
          };
        // same as 'currency' above, but display a '-' if the value is undefined
        case 'currencyOrUndefined':
          return (rowData: ListTableRowData) => {
            let value = get(rowData, column.field);
            // if the value is undefined, display a '-'
            if (value === undefined) {
              return (
                <Typography
                  style={{ fontSize: 14, fontWeight: rowData.BoldRow ? 500 : 400, marginRight: 25 }}
                >
                  -
                </Typography>
              );
            }
            // if the value is not undefined, display it's number equivalent
            if (Number.isNaN(value)) {
              value = 0;
            }
            return (
              <Typography
                style={{ fontSize: 14, fontWeight: rowData.BoldRow ? 500 : 400, marginRight: 25 }}
              >
                {currencySymbol + (Math.round(value * exchangeRate * 100) / 100).toLocaleString()}
              </Typography>
            );
          };
        // date (USE MOMENT TYPE AND CUSTOM SORT)
        // if a field2 is given, a tooltip should be shown where the difference in the dates is given
        case 'date':
          return (rowData: ListTableRowData) => {
            const timezone = this.props.session.publisher?.Timezone || 'UTC';
            const today = moment().tz(timezone).startOf('day').add(-1, 'minute');
            const date = moment(get(rowData, column.field)).tz(timezone);
            const diff = date?.clone().startOf('day').diff(today, 'days');
            let tooltipMessage = '';
            if (diff > 0) {
              tooltipMessage = `${diff} day(s) remaining`;
            } else if (diff === 0) {
              tooltipMessage = 'Ends today';
            }
            return (
              <Tooltip title={column.field2 ? tooltipMessage : ''}>
                <Typography
                  style={{
                    fontSize: 14,
                    fontWeight: rowData.BoldRow ? 500 : 400,
                    marginRight: 25,
                  }}
                >
                  {formatYearMonthNameDate(date)}
                </Typography>
              </Tooltip>
            );
          };
        // bolded text
        case 'bold':
          return (rowData: ListTableRowData) => {
            return (
              <Typography style={{ fontSize: 16, fontWeight: 700, marginRight: 25 }}>
                {get(rowData, column.field)}
              </Typography>
            );
          };
        // standard name link
        case 'namelink':
          return (rowData: ListTableRowData) => {
            const responsiveStyle: CSS.Properties = isMediumSizeOrBelow
              ? {
                  display: 'inline-block',
                  verticalAlign: 'middle',
                  width: `${RENDERTYPE_MEDIUM_WIDTH.namelink - 16}px`,
                }
              : {};

            return (
              <Button
                style={{ marginRight: 25 }}
                component={Link}
                to={this.props.getItemSettingRoute(rowData.ID)}
                onClick={() => {
                  // for mix panel event tracking
                  if (this.props.handleNameLinkClick) {
                    this.props.handleNameLinkClick(rowData.Name);
                  }
                  if (this.props.handleNameLinkClickID) {
                    this.props.handleNameLinkClickID(
                      typeof rowData.ID === 'string' ? rowData.ID : rowData.ID.toString(),
                    );
                  }
                }}
              >
                <Grid container direction='column' justify='flex-start' alignItems='flex-start'>
                  <Grid item>
                    <Typography
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'left',
                        overflowWrap: 'anywhere',
                        ...responsiveStyle,
                      }}
                    >
                      {get(rowData, column.field)}
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            );
          };
        // link does not send you out
        case 'namelink_returnsnameid':
          return (rowData: ListTableRowData) => {
            return (
              <Button
                style={{
                  marginRight: 25,
                  color: rowData.Name === 'Creatives' ? 'inherit' : publicaColors.blue,
                }}
                onClick={() => {
                  // for mix panel event tracking
                  if (this.props.handleNameLinkClick) {
                    this.props.handleNameLinkClick(rowData.Name);
                  }
                  if (this.props.handleNameLinkClickID) {
                    this.props.handleNameLinkClickID(
                      typeof rowData.ID === 'string' ? rowData.ID : rowData.ID.toString(),
                    );
                  }
                  if (this.props.handleNameLinkClickInfo) {
                    this.props.handleNameLinkClickInfo(
                      rowData.Name,
                      typeof rowData.ID === 'string' ? rowData.ID : rowData.ID.toString(),
                    );
                  }
                }}
              >
                <Grid container direction='column' justify='flex-start' alignItems='flex-start'>
                  <Grid item>
                    <Typography
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        textAlign: 'left',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {get(rowData, column.field)}
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            );
          };
        // name link which opens a modal using a callback
        case 'modal3':
          return (rowData: ListTableRowData) => {
            return (
              <Button
                style={{ minWidth: 'auto' }}
                onClick={() => {
                  if (this.props.handleOpenModal3) {
                    this.props.handleOpenModal3(rowData.ID);
                  }
                }}
              >
                <Grid style={this.props.modal3GridStyles}>
                  <Typography style={{ fontSize: 14, fontWeight: 500, textAlign: 'left' }}>
                    {get(rowData, column.field)}
                  </Typography>
                </Grid>
              </Button>
            );
          };
        case 'namelink_withtooltip':
          return (rowData: ListTableRowData) => {
            if (get(rowData, 'TooltipEnabled') === true) {
              return (
                <Tooltip title={get(rowData, 'TooltipText')}>
                  <Button
                    style={{ marginRight: 25 }}
                    component={Link}
                    to={this.props.getItemSettingRoute(rowData.ID)}
                  >
                    <Grid container direction='column' justify='center' alignItems='flex-start'>
                      <Typography style={{ fontSize: 14, fontWeight: 500, color: '#cc0000' }}>
                        {get(rowData, column.field)}
                      </Typography>
                    </Grid>
                  </Button>
                </Tooltip>
              );
            }
            return (
              <Button
                style={{ marginRight: 25 }}
                component={Link}
                to={this.props.getItemSettingRoute(rowData.ID)}
              >
                <Grid container direction='column' justify='center' alignItems='flex-start'>
                  <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                    {get(rowData, column.field)}
                  </Typography>
                </Grid>
              </Button>
            );
          };
        // copy => copies the field on click
        case 'copy':
          return (rowData: ListTableRowData) => {
            return (
              <Button style={{ marginRight: 25 }}>
                <Grid container direction='column' justify='center' alignItems='flex-start'>
                  <TooltipWithTranslation tKey='Copy'>
                    <Typography
                      style={{
                        fontSize: 12,
                        fontWeight: rowData.BoldRow ? 500 : 400,
                        cursor: 'pointer',
                      }}
                      onClick={() => copy((get(rowData, column.field) || '').toString())}
                    >
                      {get(rowData, column.field)}
                    </Typography>
                  </TooltipWithTranslation>
                </Grid>
              </Button>
            );
          };
        // overunder link requires two fields and shows the first value in larger font
        case 'overunderlink':
          return (rowData: ListTableRowData) => {
            return (
              <Button
                style={{ marginRight: 25 }}
                component={Link}
                to={this.props.getItemSettingRoute(rowData.ID)}
              >
                <Grid container direction='column' justifyContent='center' alignItems='flex-start'>
                  <Grid item>
                    <Typography style={{ fontSize: 14, fontWeight: 500 }}>
                      {get(rowData, column.field)}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography style={{ fontSize: 12, fontWeight: 400 }}>
                      {get(rowData, column.field2 || column.field)}
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            );
          };
        // switch to toggle active for the resource
        // for activeswitch, pass Active as a bool, not a number/enum
        case 'activeswitch':
          return (rowData: ListTableRowData) => {
            return (
              <Grid style={{ marginRight: 25 }}>
                <CustomSwitch
                  disabled={
                    !writeAuthorization.isAuthorized ||
                    this.props.isUpdating ||
                    rowData.IsActiveSwitchDisabled
                  }
                  checked={rowData.Active}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (this.props.handleChangeActive) {
                      this.props.handleChangeActive(rowData.ID, event.target.checked);
                    }
                  }}
                  color='secondary'
                />
              </Grid>
            );
          };
        // checkbox to toggle for the resource
        case 'markedflag':
          return (rowData: ListTableRowData) => {
            const marked = get(rowData, column.field);
            if (marked === true) {
              return (
                <Grid
                  style={{ marginRight: 25 }}
                  onClick={() => {
                    if (this.props.handleChangeItem) {
                      this.props.handleChangeItem(rowData.ID.toString(), column.field, false);
                    }
                  }}
                >
                  <TooltipWithTranslation tKey='MarkAsAvailable'>
                    <img src='/assets/shared/svg/FlagBlue.svg' style={{ width: '15px' }} />
                  </TooltipWithTranslation>
                </Grid>
              );
            } else if (marked === false) {
              return (
                <Grid
                  style={{ marginRight: 25 }}
                  onClick={() => {
                    if (this.props.handleChangeItem) {
                      this.props.handleChangeItem(rowData.ID.toString(), column.field, true);
                    }
                  }}
                >
                  <TooltipWithTranslation tKey='MarkAsBooked'>
                    <img src='/assets/shared/svg/FlagGrey.svg' style={{ width: '15px' }} />
                  </TooltipWithTranslation>
                </Grid>
              );
            } else {
              return <></>;
            }
          };
        // switch to toggle super user only for the resource
        case 'superuserswitch':
          return (rowData: ListTableRowData) => {
            return (
              <Grid style={{ marginRight: 25 }}>
                <CustomSwitch
                  disabled={!writeAuthorization.isAuthorized || this.props.isUpdating}
                  checked={'SuperUserOnly' in rowData && rowData.SuperUserOnly}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (this.props.handleSuperOnlyChange) {
                      this.props.handleSuperOnlyChange(rowData.ID, event.target.checked);
                    }
                  }}
                  color='secondary'
                />
              </Grid>
            );
          };
        // switch to toggle active for the resource
        // for activeswitch, pass Active as a bool, not a number/enum
        // this is used to bring up a modal which asks the user if they want to toggle active
        case 'activeswitchmodal':
          return (rowData: ListTableRowData) => {
            return (
              <Grid style={{ marginRight: 25 }}>
                <Switch
                  disabled={!writeAuthorization.isAuthorized || this.props.isUpdating}
                  checked={rowData.Active}
                  onChange={() => {
                    this.props.handleSetUpdating(true);
                    this.setState(
                      {
                        activeModalActive: true,
                        activeModalID: rowData.ID,
                        activeModalName: rowData.Name,
                        itemCurrentlyActive: rowData.Active ? rowData.Active : false,
                      },
                      () => this.props.handleSetUpdating(false),
                    );
                  }}
                  color='secondary'
                />
              </Grid>
            );
          };
        // delete and clone icons
        case 'deleteclone':
          return (rowData: ListTableRowData) => {
            const DisabledWithNoMessage = rowData.DisableDelete && !this.props.disableDeleteMsg;
            return (
              <Grid container direction='row' justify='center' style={{ marginRight: 25 }}>
                <Grid item>
                  <TooltipWithTranslation tKey='Clone'>
                    <IconButton
                      style={{ padding: 6 }}
                      aria-label='clone'
                      className={
                        this.props.resource
                          ? `pb-list-table-${this.props.resource}-list_iconbutton-clone-` +
                            formatForClassname(rowData.Name)
                          : ''
                      }
                      onClick={() => {
                        if (this.props.handleCloneItem) {
                          this.props.handleCloneItem(rowData.ID);
                        }
                      }}
                      disabled={!writeAuthorization.isAuthorized || this.props.isUpdating}
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </TooltipWithTranslation>
                </Grid>
                <Grid item>
                  <Tooltip
                    title={
                      rowData.DisableDelete && this.props.disableDeleteMsg
                        ? this.props.disableDeleteMsg
                        : ct('Delete')
                    }
                    disableFocusListener={DisabledWithNoMessage}
                    disableHoverListener={DisabledWithNoMessage}
                    disableTouchListener={DisabledWithNoMessage}
                  >
                    <span>
                      <IconButton
                        style={{ padding: 6 }}
                        aria-label='delete'
                        className={
                          this.props.resource
                            ? `pb-list-table-${this.props.resource}-list_iconbutton-delete-` +
                              formatForClassname(removeIDForClassname(rowData.Name))
                            : ''
                        }
                        // enables modal for item deletion
                        onClick={() => {
                          this.props.handleSetUpdating(true);
                          this.setState({
                            deleteModalActive: true,
                            deleteModalID: this.convertStringOrNumberToString(rowData.ID),
                            deleteModalName: rowData.Name,
                            deleteModalIdx: rowData.idx,
                          });
                        }}
                        disabled={
                          !writeAuthorization.isAuthorized ||
                          this.props.isUpdating ||
                          rowData.DisableDelete
                        }
                      >
                        <IconV2 name='delete' large color={publicaColors.gray} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              </Grid>
            );
          };
        // clone icons
        case 'clone':
          return (rowData: ListTableRowData) => {
            return (
              <Grid container direction='row' justify='center' style={{ marginRight: 25 }}>
                <Grid item>
                  <TooltipWithTranslation tKey='Clone'>
                    <IconButton
                      style={{ padding: 6 }}
                      aria-label='clone'
                      onClick={() => {
                        if (this.props.handleCloneItem) {
                          this.props.handleCloneItem(rowData.ID);
                        }
                      }}
                      disabled={!writeAuthorization.isAuthorized || this.props.isUpdating}
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </TooltipWithTranslation>
                </Grid>
              </Grid>
            );
          };
        // delete icon
        case 'delete':
          return (rowData: ListTableRowData) => {
            return (
              <Grid container direction='row' justify='center'>
                <Tooltip
                  title={
                    rowData.DisableDelete && this.props.disableDeleteMsg
                      ? this.props.disableDeleteMsg
                      : ct('Delete')
                  }
                >
                  <Grid item style={{ marginRight: 25 }}>
                    <IconButton
                      style={{ padding: 6 }}
                      aria-label='delete'
                      // enables modal for item deletion
                      onClick={() => {
                        this.props.handleSetUpdating(true);
                        let deleteModalName: string;
                        const rowDataObject = Object.assign(rowData, {});
                        if (rowDataObject.hasOwnProperty('DemandSource')) {
                          // @ts-ignore
                          deleteModalName = rowDataObject.DemandSource;
                        } else {
                          deleteModalName = rowData.Name;
                        }
                        this.setState({
                          deleteModalActive: true,
                          deleteModalID: this.convertStringOrNumberToString(rowData.ID),
                          deleteModalName,
                          deleteModalIdx: rowData.idx,
                        });
                      }}
                      disabled={
                        !writeAuthorization.isAuthorized ||
                        this.props.isUpdating ||
                        rowData.DisableDelete
                      }
                    >
                      <IconV2 name='delete' large color={publicaColors.gray} />
                    </IconButton>
                  </Grid>
                </Tooltip>
              </Grid>
            );
          };
        // convert a string list into multiple chips with text
        case 'textchips':
          return (rowData: ListTableRowData) => {
            const stringList = get(rowData, column.field) as string[];
            return (
              <Grid container direction='row' justify='center'>
                {stringList.map((str: string) => (
                  <Grid item style={{ marginLeft: '5px', marginRight: '5px' }}>
                    <ChipInventory label={str} />
                  </Grid>
                ))}
              </Grid>
            );
          };
        case 'icons':
          return (rowData: ListTableRowData) => {
            if ('Icons' in rowData) {
              return (
                <Grid container direction='row' justify='center' alignItems='center'>
                  {rowData.Icons.map((iconKey: IconMapKey) => (
                    <Grid item style={{ marginLeft: '5px', marginRight: '5px' }}>
                      {iconMap[iconKey]}
                    </Grid>
                  ))}
                </Grid>
              );
            } else {
              return <></>;
            }
          };
        case 'actions':
          return (rowData: ListTableRowData) => {
            return (
              <Grid container direction='row' justify='center'>
                {/* Edit Modal popup action */}
                <Grid item style={{ marginRight: 25 }}>
                  <Tooltip
                    title={
                      rowData.DisableEditModal && this.props.disableEditModalMsg
                        ? this.props.disableEditModalMsg
                        : ct('Edit')
                    }
                  >
                    <IconButton
                      style={{ padding: 6 }}
                      aria-label='edit'
                      // opens modal for edit
                      onClick={() => {
                        if (this.props.handleOpenModal3) {
                          this.props.handleOpenModal3(rowData.ID);
                        }
                      }}
                      disabled={
                        !writeAuthorization.isAuthorized ||
                        this.props.isUpdating ||
                        rowData.DisableEditModal
                      }
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  {/* Delete Row action */}
                  <Tooltip
                    title={
                      rowData.DisableDelete && this.props.disableDeleteMsg
                        ? this.props.disableDeleteMsg
                        : ct('Delete')
                    }
                  >
                    <IconButton
                      style={{ padding: 6 }}
                      aria-label='delete'
                      className={
                        this.props.resource
                          ? `pb-${this.props.resource}-list_iconbutton-delete-` +
                            formatForClassname(rowData.Name)
                          : ''
                      }
                      // enables modal for item deletion
                      onClick={() => {
                        this.props.handleSetUpdating(true);
                        this.setState({
                          deleteModalActive: true,
                          deleteModalID: this.convertStringOrNumberToString(rowData.ID),
                          deleteModalName: rowData.Name,
                          deleteModalIdx: rowData.idx,
                        });
                      }}
                      disabled={
                        !writeAuthorization.isAuthorized ||
                        this.props.isUpdating ||
                        rowData.DisableDelete
                      }
                    >
                      <IconV2 name='delete' large color={publicaColors.gray} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            );
          };
        default:
          // if no renderType is defined, no render function will be returned
          // this results in a default render by material-table
          return undefined;
      }
    }

    // -------- HANDLER FUNCTIONS -------- //
    // NOTE: functions passed into a child component which update state in the parent
    // should be called handleFunc and used to not directly modify the props/state
    //

    handleCloseDeleteDialog = (): void => {
      this.props.handleSetUpdating(false);
      this.setState({
        deleteModalActive: false,
        deleteModalID: '',
        deleteModalName: '',
      });
    };

    handleCloseActiveDialog = (): void => {
      this.props.handleSetUpdating(false);
      this.setState({
        activeModalActive: false,
        activeModalID: '',
        activeModalName: '',
      });
    };

    renderAddButton = (): ReactElement => {
      return (
        <Grid item>
          <Button className={this.props.classes.button} style={{ width: 135 }}>
            <Grid container direction='row' alignItems='center' spacing={1}>
              <Grid item>
                <AddIcon
                  style={{
                    marginTop: 5,
                    color: getColorTheme('primaryButtons'), // Color for upload button of Campaign Settings
                  }}
                />
              </Grid>
              <Grid item>
                <Typography className={this.props.classes.menuButtonText}>Add Row</Typography>
              </Grid>
            </Grid>
          </Button>
        </Grid>
      );
    };

    // Handle when the user clicks on the play button in the 'vastPlay' column.
    handleVastPlayButtonClick(vastURL: string) {
      getMediafileFromVastURL(vastURL)
        .then((mediafileURL: string) => {
          // Success, open the video in a new tab.
          window.open(mediafileURL, '_blank');
        })
        .catch((reason: Error) => {
          // Error, try to handle it in the parent component.
          if (this.props.handleError) {
            this.props.handleError(reason.message);
          }
        });
    }

    // -------- RENDER FUNCTIONS -------- //
    render() {
      // NOTE: We destructure props and states used in render functions to clearly
      // show what we are passing as well as keeping the code clean and easier to read
      const {
        title,
        data,
        columns,
        selectionEnabled,
        handleSelectionChange,
        classes,
        fixedEnabled,
        enableSearchBarLeft,
        renderCustomToolbar,
        pageSize,
        pageSizeOptions,
        useCustomAddButton,
        renderCustomHeader,
        readRoleName,
        writeRoleName,
        editable,
        actionsColumnIndex,
        enableCenterActionCells,
        emptyRowsWhenPaging,
        enablePagination,
        queryData,
      } = this.props;

      // since we are in a class component, we must pass in baseState via this.context.
      // this causes 'base' to be unknown so we ignore
      // @ts-ignore
      const base: IBaseState = this.context;
      const isMediumSizeOrBelow = isWithinMediaSizeOrBelow('medium', base.mediaSize);

      // Check if the user has the read permission. If the prop is undefined we assume the user always has access.
      let readAuthorization = allowedEmptyIPermissionCheck; // When the array is empty, user has right.
      if (readRoleName !== undefined) {
        readAuthorization = IS_AUTHORIZED([readRoleName], this.props.user.user); // Role is defined, get 'real' array value.
      }

      // process with data, fill each row with its index

      const d = enablePagination && queryData ? queryData : cloneDeep(data);
      if (!enablePagination && d instanceof Array) {
        d.forEach((_: any, idx: number) => {
          d[idx].idx = idx;
        });
      }

      if (!readAuthorization.isAuthorized) {
        // if user does not have read rights
        return (
          <Grid>
            <ModelMissingPermission missingPermissions={readAuthorization.missingPermissions} />
          </Grid>
        );
      }

      const cols = cloneDeep(columns);
      // loop over columns to enrich the render field
      for (let i = 0; i < cols.length; i++) {
        const renderType = cols[i].renderType || '';
        const colWidth = cols[i].width;
        const width = getListTableMediumResponsiveColumnWidth(
          renderType,
          colWidth,
          isMediumSizeOrBelow,
        );

        const columnRender = this.generateRender(cols[i], writeRoleName, isMediumSizeOrBelow);
        if (columnRender) {
          // @ts-ignore
          cols[i].render = columnRender;
        }
        // center all cols
        cols[i].cellStyle = {
          textAlign: cols[i].textAlign
            ? cols[i].textAlign
            : cols[i].field === 'Name'
            ? 'left'
            : 'center',
          padding: 6,
          minWidth: cols[i].minWidth ? cols[i].minWidth : '',
          maxWidth: cols[i].maxWidth ? cols[i].maxWidth : '',
          // YOU CAN SET A FIXED WIDTH WITH PERCENTAGES WHICH IS REALLY NICE
          // use the fixedEnabled in props!
          width,
          overflow: 'hidden',
          ...cols[i].cellStyle,
        };
        cols[i].headerStyle = {
          textAlign: cols[i].textAlignHeader ? cols[i].textAlignHeader : 'center',
          padding: 6,
          paddingLeft: cols[i].paddingLeft ? cols[i].paddingLeft : 6,
          minWidth: cols[i].minWidth ? cols[i].minWidth : '',
          maxWidth: cols[i].maxWidth ? cols[i].maxWidth : '',
          // YOU CAN SET A FIXED WIDTH WITH PERCENTAGES WHICH IS REALLY NICE
          // use the fixedEnabled in props!
          width,
        };
        if (width) {
          cols[i].width = width;
        }
      }
      if (this.props.isLoading) {
        return (
          <MaterialTable
            isLoading={true}
            title={title}
            columns={cols}
            data={[]}
            style={{
              paddingLeft: '20px',
              paddingRight: '20px',
              borderRadius: '5px',
            }}
            localization={{
              toolbar: {
                searchPlaceholder: ct('Search'),
                searchTooltip: ct('Search'),
              },
              body: {
                emptyDataSourceMessage: ct('NoDataFound'),
              },
            }}
          />
        );
      }

      let fieldProps = {
        style: {
          paddingLeft: '20px',
          paddingRight: '20px',
          borderRadius: '5px',
        },
        title,
        columns: cols,
        editable,
        options: {
          thirdSortClick: false,
          debounceInterval: 500,
          actionsCellStyle: enableCenterActionCells
            ? { justifyContent: 'center', backgroundColor: 'blue' }
            : {},
          actionsColumnIndex: actionsColumnIndex ? actionsColumnIndex : 0,
          padding: 'dense',
          searchFieldAlignment: enableSearchBarLeft ? 'left' : 'right',
          searchFieldVariant: '',
          searchFieldStyle: {
            backgroundColor: '#F8F8F8',
            fontSize: '12px',
            color: '#707070',
            paddingTop: '2px',
            paddingLeft: '5px',
            marginLeft: enableSearchBarLeft ? '-40px' : '0px',
          },
          emptyRowsWhenPaging,
          selection: !!selectionEnabled,
          showTextRowsSelected: false,
          tableLayout: fixedEnabled ? 'fixed' : 'auto',
          width: '250px',
          pageSize: pageSize ? pageSize : 10,
          pageSizeOptions,
          rowStyle: (rowData: any) => ({
            backgroundColor: rowData.BoldRow ? '#EEE' : '#FFF',
          }),
        },
        localization: {
          toolbar: {
            searchPlaceholder: ct('Search'),
            searchTooltip: ct('Search'),
          },
          body: {
            emptyDataSourceMessage: ct('NoDataFound'),
          },
        },
        data: d,
      };

      let components = {};
      if (renderCustomToolbar) {
        components = Object.assign(components, {
          Toolbar: renderCustomToolbar,
        });
      }
      if (renderCustomHeader) {
        components = Object.assign(components, {
          Header: renderCustomHeader,
        });
      }
      if (enableCenterActionCells) {
        components = Object.assign(components, {
          EditRow: (props: any) => <MTableEditRow {...props} className={classes.tableRow} />,
        });
      }
      if (this.props.resource) {
        components = Object.assign(components, {
          Row: (props: any) => {
            return (
              <MTableBodyRow {...props} className={`pb-${this.props.resource}-list_tablebodyrow`} />
            );
          },
          Body: (props: any) => {
            return <MTableBody {...props} className={`pb-${this.props.resource}-list_tablebody`} />;
          },
        });
      }

      fieldProps = Object.assign(fieldProps, {
        components,
      });

      // separated as variable for ts-ignore
      const materialTable = (
        // @ts-ignore
        <MaterialTable
          {...fieldProps}
          onOrderChange={this.props.onOrderChange}
          tableRef={this.props.enablePagination && this.props.tableRef}
          // @ts-ignore
          icons={useCustomAddButton ? { Add: this.renderAddButton } : {}}
          onSelectionChange={(rows: ListTableRowData[], selectedRow: ListTableRowData) => {
            if (handleSelectionChange) {
              handleSelectionChange(rows, selectedRow);
            }
          }}
        />
      );

      return (
        <Grid item style={{ width: '100%' }}>
          {materialTable}
          <ModelDialogDeleteView
            active={this.state.deleteModalActive}
            model={this.props.title}
            idx={this.state.deleteModalIdx}
            ID={this.state.deleteModalID}
            Name={this.state.deleteModalName}
            disableSnackbar={this.props.disableSnackbar}
            deleteModel={(id: string, idx?: number): Promise<any> => {
              if (this.props.handleDeleteItem) {
                return this.props.handleDeleteItem(id, idx);
              }
              return Promise.resolve();
            }}
            onDelete={this.handleCloseDeleteDialog}
            onError={this.handleCloseDeleteDialog}
            onClose={this.handleCloseDeleteDialog}
          />
          <ModelDialogActiveView
            active={this.state.activeModalActive}
            ID={this.state.activeModalID}
            name={this.state.activeModalName}
            // @ts-ignore
            activeModel={(id: string) => {
              if (this.props.handleActiveItem) {
                return this.props.handleActiveItem(id);
              }
            }}
            onDelete={this.handleCloseActiveDialog}
            onError={this.handleCloseActiveDialog}
            onClose={this.handleCloseActiveDialog}
            currentlyActive={this.state.itemCurrentlyActive}
          />
        </Grid>
      );
    }
  },
);
